import Axios from "axios";
import { fbAuth } from "../firebase/firebase";
import _environment from "../_environment/_environment";
import { RedirectAuth } from "./utils";

/**
 * The response used to be base64-encoded wholesale and parked in localStorage,
 * from where it was appended to the next app's URL - a live Firebase custom
 * token, the legacy token and the chat credentials, readable by anyone with the
 * URL. The backend now also returns a single-use `handle` that stands in for
 * that payload, and only the handle travels.
 */
export const loginWithCredentials = async (
  email: string,
  password: string
): Promise<any> => {
  try {
    console.log("auth", "Sign in with credentials...");
    const res = await Axios.post(
      `${_environment.baseApiURL}${_environment.endpoints.login}`,
      { email, password }
    );

    const { handle, token, role, is_fully_allowed, version } = res.data.data;

    return { handle, token, role, is_fully_allowed, version };
  } catch (err) {
    throw new Error(err.response.data.message);
  }
};

/**
 * Mint a fresh handle for the user who is already signed in here.
 *
 * The redirect paths that fire on an existing session - the /redirect route and
 * the onAuthStateChanged listener - have no login response to draw a handle
 * from, and used to reach into localStorage for the stored blob. A single-use
 * handle cannot be replayed from storage, so they ask the backend for a new one
 * against the current Firebase session instead.
 *
 * Returns `{}` rather than throwing, so a caller can decide what to do. Callers
 * MUST check for a missing `handle` and sign the user out instead of redirecting:
 * an empty result means we have nothing to hand over, and redirecting anyway
 * sends the user to an app that bounces them straight back here - where the
 * persisted Firebase session starts the same failing mint again.
 *
 * `role`/`is_fully_allowed`/`version` come back alongside the handle, because
 * shouldOnboard needs them to choose between the webapp and the onboarding flow
 * and there is no stored blob left to read them from. They are only ever absent
 * when the mint itself failed, which is the sign-out case above - so an
 * unfinished brand is never silently routed to the webapp.
 */
export const mintHandle = async (): Promise<RedirectAuth> => {
  try {
    const idToken = await fbAuth().currentUser?.getIdToken();

    if (!idToken) {
      return {};
    }

    const res = await Axios.post(
      `${_environment.baseApiURL}auth/handle`,
      {},
      { headers: { ia_auth_token: idToken } }
    );

    const { handle, role, is_fully_allowed, version } = res.data.data;

    return { handle, role, is_fully_allowed, version };
  } catch (err) {
    console.error("auth", "could not mint handle", err);
    return {};
  }
};

export const forgotPassword = async (email: string): Promise<any> => {
  try {
    await Axios.post(`${_environment.baseApiURL}auth/forgot-password`, {
      email,
    });

    return true;
  } catch (err) {
    throw err;
  }
};

export const resetPassword = async (
  token: string,
  password: string
): Promise<any> => {
  try {
    await Axios.post(`${_environment.baseApiURL}auth/reset-password`, {
      token,
      password,
    });

    return true;
  } catch (err) {
    throw err;
  }
};

export const signOut = () => {
  console.log("auth", "logout");
  localStorage.removeItem("authCode");
  fbAuth().signOut();
};

export const getProfile = async (): Promise<any> => {
  try {
    const res = await Axios.get(`${_environment.baseApiURL}users/me/profile`, {
      headers: {
        ia_auth_token: await fbAuth().currentUser?.getIdToken(),
      },
    });

    return res.data.data;
  } catch (err) {
    throw err;
  }
};
